package com.fireavert.administration.super_admin_tools_page.client

import com.fireavert.administration.super_admin_tools_page.data_models.data_model.SmokeAlarmDurationRecordModel
import com.fireavert.administration.super_admin_tools_page.data_models.models.PropertyNotificationStatsModel
import com.fireavert.administration.super_admin_tools_page.data_models.models.PropertyOfflineStatisticsModel
import com.fireavert.administration.super_admin_tools_page.data_models.requests.SuperAdminSettingsRequest
import com.fireavert.administration.super_admin_tools_page.data_models.requests.TestEmailReportRequest
import com.fireavert.administration.super_admin_tools_page.interface_adapters.WirelessIdRepositoryDataSource
import com.fireavert.common.Try
import com.fireavert.devices.data_models.response.GetDeviceRecentGatewayAssociationResponse
import com.fireavert.devices.data_models.response.WirelessDeviceIdResponse
import com.fireavert.logging.Logger
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.serialization.json.Json

class WirelessIdRepositoryDataSourceImpl(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val logger: Logger
): WirelessIdRepositoryDataSource {
    override suspend fun loadWirelessIdData(deviceId: String, token: String): Try<WirelessDeviceIdResponse> {
        return Try.fromCoCallable {
            val response = httpClient.get {
                header("Authorization", "Bearer $token")
                url("$baseUrl/devices/wirelessId/$deviceId")
            }
            if (response.status.isSuccess()) response.body()
            else throw ClientRequestException(response, response.body())
        }
    }

    override suspend fun loadGatewayDeviceData(propertyId: String, token: String): Try<GetDeviceRecentGatewayAssociationResponse> {
        return Try.fromCoCallable {
            val response = httpClient.get {
                header("Authorization", "Bearer $token")
                url("$baseUrl/devices/lastGatewayConnected/$propertyId")
            }
            if (response.status.isSuccess()) response.body()
            else throw ClientRequestException(response, response.body())
        }
    }

    override suspend fun reloadAllGatewayInfo(token: String): Try<Boolean> {
        return Try.fromCoCallable {
            val response = httpClient.get {
                header("Authorization", "Bearer $token")
                url("$baseUrl/gateways/reloadAllGatewayInfo")
            }
            if (response.status.isSuccess()) response.body()
            else throw ClientRequestException(response, response.body())
        }
    }

    override suspend fun loadPropertyStatistics(token: String): Try<List<PropertyOfflineStatisticsModel>> {
        return Try.fromCoCallable {
            val response = httpClient.get {
                header("Authorization", "Bearer $token")
                url("$baseUrl/devices/offlineStatistics")
            }
            if (response.status.isSuccess()) response.body()
            else throw ClientRequestException(response, response.body())
        }
    }

    override suspend fun loadPropertyNotificationStats(token: String): Try<List<PropertyNotificationStatsModel>> {
        return Try.fromCoCallable {
            val response = httpClient.get {
                header("Authorization", "Bearer $token")
                url("$baseUrl/properties/propertyNotificationStats")
            }
            if (response.status.isSuccess()) response.body()
            else throw ClientRequestException(response, response.body())
        }
    }

    override suspend fun loadPropertySmokeAlarmDurationStats(token: String): Try<List<SmokeAlarmDurationRecordModel>> {
        return Try.fromCoCallable {
            val response = httpClient.get {
                header("Authorization", "Bearer $token")
                url("$baseUrl/events/smokeAlarmDurationStats")
            }
            if (response.status.isSuccess()) response.body()
            else throw ClientRequestException(response, response.body())
        }
    }

    override suspend fun checkAppsheetDatabaseHealth(token: String): Try<Boolean> {
        return Try.fromCoCallable {
            val response = httpClient.get {
                header("Authorization", "Bearer $token")
                url("$baseUrl/appsheet/health")
            }
            if (response.status.isSuccess()) response.body()
            else throw ClientRequestException(response, response.body())
        }
    }

    override suspend fun sendTestEmail(
        request: TestEmailReportRequest,
        token: String
    ): Try<Boolean> {
        return Try.fromCoCallable {
            val response = httpClient.post {
                header("Authorization", "Bearer $token")
                url("$baseUrl/admin/debug/testEmailReport")
                setBody(request)
                contentType(ContentType.Application.Json)
            }
            if (response.status.isSuccess()) response.body()
            else throw ClientRequestException(response, response.body())
        }
    }

    override suspend fun saveOfflineNotificationSettings(
        dataModel: SuperAdminSettingsRequest,
        token: String
    ): Try<Boolean> {
        return Try.fromCoCallable {
            logger.i("Attempting to save settings. Payload: ${(Json.configuration)}")
            val response = httpClient.post {
                header("Authorization", "Bearer $token")
                url("$baseUrl/superAdminSettings/saveSettings")
                setBody(dataModel)
                contentType(ContentType.Application.Json)
            }
            if (response.status.isSuccess()) response.body()
            else throw ClientRequestException(response, response.body())
        }
    }

    override suspend fun getOfflineNotificationSettings(
        token: String
    ): Try<SuperAdminSettingsRequest> {
        return Try.fromCoCallable {

            val response = httpClient.get {
                header("Authorization", "Bearer $token")
                url("$baseUrl/superAdminSettings/getSettings")
            }
            logger.i("Response status: ${response.status}")
            if (response.status.isSuccess()) response.body()
            else throw ClientRequestException(response, response.body())
        }
    }
}